/**
 * @module features/display-module/api/ddmApi
 * @description TanStack Query v5 hooks and API functions for Digital Display Module (DDM)
 * device listing, display artifact CRUD, and ULID-keyed Storage Bucket media uploads (`T015`).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { apiClient, AppError, ApiSuccessEnvelope } from "@/lib/api-client";
import type {
  AssignedDdmDeviceSummary,
  DdmDeleteNotificationResponse,
  DdmNotificationListFilterParams,
  DdmNotificationRecord,
} from "../types/ddm";

export interface PaginatedDdmNotificationList {
  items: DdmNotificationRecord[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

interface EnvelopeWithPagination<T> extends ApiSuccessEnvelope<T> {
  pagination?: PaginatedDdmNotificationList["pagination"];
}

export const ddmKeys = {
  all: ["ddm"] as const,
  devices: (clientId?: string) =>
    [...ddmKeys.all, "assigned-devices", clientId ?? "self"] as const,
  lists: () => [...ddmKeys.all, "notifications"] as const,
  list: (params: DdmNotificationListFilterParams) =>
    [...ddmKeys.lists(), params] as const,
  detail: (id: string) => [...ddmKeys.all, "notification", id] as const,
};

/**
 * Fetch actively assigned Digital Display Module (`display_module`) devices.
 */
export async function fetchAssignedDdmDevices(
  clientId?: string
): Promise<AssignedDdmDeviceSummary[]> {
  try {
    const response = await apiClient.get<
      ApiSuccessEnvelope<AssignedDdmDeviceSummary[]>
    >("/ddm/assigned-devices/", {
      params: clientId && clientId !== "all" ? { client_id: clientId } : undefined,
    });
    return response.data.data ?? [];
  } catch (error) {
    throw AppError.fromUnknown(
      error,
      "Unable to load assigned Digital Display Module devices."
    );
  }
}

/**
 * Fetch paginated DDM display artifacts with live delivery status.
 */
export async function fetchDdmNotifications(
  params: DdmNotificationListFilterParams
): Promise<PaginatedDdmNotificationList> {
  try {
    const queryParams: Record<string, string | number> = {
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    };
    if (params.client_id && params.client_id !== "all") {
      queryParams.client_id = params.client_id;
    }
    if (params.device_id && params.device_id !== "all") {
      queryParams.device_id = params.device_id;
    }
    if (params.artifact_type && params.artifact_type !== "all") {
      queryParams.artifact_type = params.artifact_type;
    }
    if (params.delivery_status && params.delivery_status !== "all") {
      queryParams.delivery_status = params.delivery_status;
    }
    if (params.search && params.search.trim()) {
      queryParams.search = params.search.trim();
    }
    if (params.start_timestamp) {
      queryParams.start_timestamp = params.start_timestamp;
    }
    if (params.end_timestamp) {
      queryParams.end_timestamp = params.end_timestamp;
    }

    const response = await apiClient.get<
      EnvelopeWithPagination<DdmNotificationRecord[]>
    >("/ddm/notifications/", { params: queryParams });

    const items = response.data.data ?? [];
    const pagination = response.data.pagination ?? {
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      total_items: items.length,
      total_pages: 1,
      has_next: false,
      has_previous: false,
    };
    return { items, pagination };
  } catch (error) {
    throw AppError.fromUnknown(error, "Unable to load display artifacts.");
  }
}

export interface CreateOrUpdateDdmArtifactInput {
  payload: Record<string, unknown>;
  imageFile?: File | null;
  videoFile?: File | null;
  galleryFiles?: File[];
  videoDurationSeconds?: number | null;
}

function buildArtifactFormData(input: CreateOrUpdateDdmArtifactInput): FormData {
  const formData = new FormData();
  formData.append("payload", JSON.stringify(input.payload));
  if (input.imageFile) {
    formData.append("image_file", input.imageFile);
  }
  if (input.videoFile) {
    formData.append("video_file", input.videoFile);
  }
  if (input.galleryFiles && input.galleryFiles.length > 0) {
    for (const gf of input.galleryFiles) {
      formData.append("gallery_files", gf);
    }
  }
  if (
    input.videoDurationSeconds !== undefined &&
    input.videoDurationSeconds !== null
  ) {
    formData.append("video_duration_seconds", String(input.videoDurationSeconds));
  }
  return formData;
}

/**
 * Create a new DDM display artifact (`version = 1`), optionally uploading media via Record-First ULID flow.
 */
export async function createDdmNotification(
  input: CreateOrUpdateDdmArtifactInput
): Promise<DdmNotificationRecord> {
  try {
    const hasFiles =
      Boolean(input.imageFile) ||
      Boolean(input.videoFile) ||
      Boolean(input.galleryFiles && input.galleryFiles.length > 0);

    if (hasFiles) {
      const formData = buildArtifactFormData(input);
      const response = await apiClient.post<
        ApiSuccessEnvelope<DdmNotificationRecord>
      >("/ddm/notifications/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.data;
    }

    const response = await apiClient.post<
      ApiSuccessEnvelope<DdmNotificationRecord>
    >("/ddm/notifications/", input.payload);
    return response.data.data;
  } catch (error) {
    throw AppError.fromUnknown(error, "Failed to create display artifact.");
  }
}

/**
 * Update an existing DDM display artifact (`version = version + 1`, `is_acknowledged = false`).
 */
export async function updateDdmNotification(
  id: string,
  input: CreateOrUpdateDdmArtifactInput
): Promise<DdmNotificationRecord> {
  try {
    const hasFiles =
      Boolean(input.imageFile) ||
      Boolean(input.videoFile) ||
      Boolean(input.galleryFiles && input.galleryFiles.length > 0);

    if (hasFiles) {
      const formData = buildArtifactFormData(input);
      const response = await apiClient.patch<
        ApiSuccessEnvelope<DdmNotificationRecord>
      >(`/ddm/notifications/${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.data;
    }

    const response = await apiClient.patch<
      ApiSuccessEnvelope<DdmNotificationRecord>
    >(`/ddm/notifications/${id}/`, input.payload);
    return response.data.data;
  } catch (error) {
    throw AppError.fromUnknown(error, "Failed to update display artifact.");
  }
}

/**
 * Delete a DDM display artifact (queuing a versioned tombstone if previously acknowledged by the device).
 */
export async function deleteDdmNotification(
  id: string
): Promise<DdmDeleteNotificationResponse> {
  try {
    const response = await apiClient.delete<
      ApiSuccessEnvelope<DdmDeleteNotificationResponse>
    >(`/ddm/notifications/${id}/`);
    return response.data.data;
  } catch (error) {
    throw AppError.fromUnknown(error, "Failed to delete display artifact.");
  }
}

/**
 * Upload media directly to an existing notification by its ULID primary key.
 */
export async function uploadDdmNotificationMedia(params: {
  notificationId: string;
  files: File[];
  mediaType: "image" | "video";
  videoDurationSeconds?: number | null;
}): Promise<DdmNotificationRecord> {
  try {
    const formData = new FormData();
    formData.append("media_type", params.mediaType);
    for (const file of params.files) {
      formData.append("files", file);
    }
    if (
      params.videoDurationSeconds !== undefined &&
      params.videoDurationSeconds !== null
    ) {
      formData.append(
        "video_duration_seconds",
        String(params.videoDurationSeconds)
      );
    }
    const response = await apiClient.post<
      ApiSuccessEnvelope<DdmNotificationRecord>
    >(`/ddm/notifications/${params.notificationId}/media/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  } catch (error) {
    throw AppError.fromUnknown(
      error,
      "Failed to upload media to Storage Bucket."
    );
  }
}

/**
 * Hook to list assigned DDM devices for the current organization (or filtered client for Super Admin).
 */
export function useAssignedDdmDevices(clientId?: string) {
  return useQuery({
    queryKey: ddmKeys.devices(clientId),
    queryFn: () => fetchAssignedDdmDevices(clientId),
    staleTime: 30_000,
  });
}

/**
 * Hook to list paginated DDM display artifacts with automatic 15s polling for live device ack updates.
 */
export function useDdmNotifications(params: DdmNotificationListFilterParams) {
  return useQuery({
    queryKey: ddmKeys.list(params),
    queryFn: () => fetchDdmNotifications(params),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
}

/**
 * Mutation hook to create a DDM display artifact.
 */
export function useCreateDdmNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrUpdateDdmArtifactInput) =>
      createDdmNotification(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ddmKeys.lists() });
    },
  });
}

/**
 * Mutation hook to update a DDM display artifact (`version + 1`).
 */
export function useUpdateDdmNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: CreateOrUpdateDdmArtifactInput;
    }) => updateDdmNotification(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ddmKeys.lists() });
    },
  });
}

/**
 * Mutation hook to soft-delete a DDM display artifact.
 */
export function useDeleteDdmNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDdmNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ddmKeys.lists() });
    },
  });
}

/**
 * Mutation hook to upload media to an existing DDM display artifact.
 */
export function useUploadDdmMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadDdmNotificationMedia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ddmKeys.lists() });
    },
  });
}

